var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/store.ts
import { createStore } from "zustand/vanilla";
import { shallow } from "zustand/vanilla/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { useStore } from "zustand";
import { produce } from "immer";
import get from "lodash/get";
import mergeWith from "lodash/mergeWith";

// src/league-config-error.ts
var LeagueConfigError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "LeagueConfigError";
  }
};

// src/league-config-schema.ts
import {
  type as type3,
  intersection as intersection2,
  record as record4,
  string as string13,
  unknown as unknown2,
  optional as optional16,
  object as object17
} from "superstruct";

// src/module-config-schemas/dashboard.ts
import { boolean, object, optional } from "superstruct";
var dashboardConfigSchema = object({
  showFullTimeStamp: optional(boolean())
});

// src/module-config-schemas/core.ts
import {
  object as object5,
  optional as optional5,
  string as string3,
  boolean as boolean5,
  enums as enums2,
  func as func4,
  intersection,
  record,
  type,
  deprecated as deprecated2
} from "superstruct";

// src/module-config-schemas/auth.ts
import {
  object as object2,
  union,
  optional as optional2,
  func,
  number,
  boolean as boolean2
} from "superstruct";
var authStandaloneConfigSchema = object2({
  clientOptions: object2(),
  idleSessionLifetimeHours: number(),
  redirectLoginOptions: optional2(
    object2()
  ),
  sessionTimeoutOptions: optional2(
    object2({
      onSessionExpire: optional2(func()),
      onSessionExtend: optional2(func()),
      forceIdleSessionLogout: optional2(boolean2())
    })
  )
});
var authEmbeddedConfigSchema = object2({
  getToken: func(),
  getUserIdFromMobileHandoff: optional2(
    func()
  )
});
var authConfigSchema = union([
  authStandaloneConfigSchema,
  authEmbeddedConfigSchema
]);

// src/module-config-schemas/ui.ts
import {
  boolean as boolean3,
  enums,
  literal,
  object as object3,
  optional as optional3,
  string,
  union as union2,
  func as func2
} from "superstruct";
var uiConfiSchema = object3({
  theme: object3(),
  themeOptions: optional3(object3()),
  components: optional3(
    object3({
      pageHeader: optional3(
        object3({
          shape: optional3(enums(["swoop", "line"])),
          backgroundColor: optional3(string())
        })
      ),
      loading: optional3(
        object3({
          element: object3(),
          text: optional3(union2([literal(false), string()]))
        })
      ),
      toastMessage: optional3(
        object3({
          component: func2()
        })
      )
    })
  ),
  groupLogo: optional3(
    object3({
      element: optional3(object3())
    })
  ),
  features: optional3(
    object3({
      pointsEnabled: optional3(boolean3())
    })
  )
});

// src/module-config-schemas/analytics.ts
import {
  deprecated,
  func as func3,
  object as object4,
  optional as optional4,
  string as string2,
  boolean as boolean4
} from "superstruct";
var analyticsClientConfigSchema = object4({
  GAPropertyId: deprecated(
    optional4(string2()),
    () => console.warn(
      "This property is no longer supported, you can safely remove it"
    )
  ),
  segmentKey: deprecated(
    optional4(string2()),
    () => console.warn(
      "This property is no longer supported on the client side. Please use server-driven configurations"
    )
  ),
  onIdentify: optional4(func3()),
  onTrackEvent: optional4(
    func3()
  ),
  onTrackPageViewEvent: deprecated(
    optional4(
      func3()
    ),
    () => (
      // eslint-disable-next-line no-console
      console.warn(
        "onTrackPageViewEvent is deprecated. Please use onTrackEvent instead."
      )
    )
  ),
  shouldEnableLocalAnalyticsLogging: optional4(boolean4())
});

// src/module-config-schemas/core.ts
var coreConfigSchema = object5({
  clientId: string3(),
  /**
   * @deprecated TEMPORARY while we transition from client-set TenantID to server-set
   */
  tenantId: optional5(string3()),
  appVersion: optional5(string3()),
  contentUrl: string3(),
  analytics: optional5(analyticsClientConfigSchema),
  api: object5({
    url: string3(),
    wsUrl: string3(),
    legacyRestApi: string3()
  }),
  // TODO: make required once no app (i.e., B2B) ever needs to initialize witohut auth
  auth: optional5(authConfigSchema),
  appEnvironment: enums2(["production", "staging", "development"]),
  customMethods: object5({
    handleLink: func4()
  }),
  routing: optional5(
    object5({
      isUsingLeagueRouting: optional5(boolean5())
    })
  ),
  mobileAppUrls: deprecated2(
    optional5(
      object5({
        appStoreUrl: optional5(string3()),
        playStoreUrl: optional5(string3())
      })
    ),
    () => console.warn(
      "This property is no longer supported on the client side. Please use server-driven configurations"
    )
  ),
  i18n: optional5(
    object5({
      strings: optional5(
        intersection([
          type({
            default: record(string3(), string3())
          }),
          record(string3(), record(string3(), string3()))
        ])
      ),
      stringOverrides: deprecated2(
        optional5(
          intersection([
            type({
              default: record(string3(), string3())
            }),
            record(string3(), record(string3(), string3()))
          ])
        ),
        (value, ctx) => console.warn(
          `${ctx.path} is deprecated, but value was '${value}'. Please use 'strings' instead.`
        )
      )
    })
  ),
  ui: uiConfiSchema,
  onError: optional5(func4())
});

// src/module-config-schemas/asset-config.ts
import { object as object6, string as string4, optional as optional6, record as record2, unknown } from "superstruct";
var assetConfigConfigSchema = object6({
  overrides: optional6(record2(string4(), unknown()))
});

// src/module-config-schemas/benefits.ts
import { object as object7, optional as optional7, string as string5 } from "superstruct";
var benefitsConfigSchema = object7({
  benefitsLandingMasonryConfigKey: optional7(string5()),
  benefitsDetailsMasonryConfigKey: optional7(string5())
});

// src/module-config-schemas/example-module.ts
import { object as object8, string as string6, boolean as boolean6, optional as optional8 } from "superstruct";
var exampleModuleConfigSchema = object8({
  someRequiredProperty: string6(),
  someOptionalFeatureFlag: optional8(boolean6())
});

// src/module-config-schemas/care-collections.ts
import { boolean as boolean7, object as object9, optional as optional9, string as string7 } from "superstruct";
var careCollectionsConfigSchema = object9({
  showCareCollectionFeedbackComponent: optional9(boolean7()),
  queryTagForCollectionsTopics: optional9(string7())
});

// src/module-config-schemas/claims.ts
import {
  boolean as boolean8,
  object as object10,
  partial,
  optional as optional10,
  string as string8,
  any,
  record as record3,
  func as func5
} from "superstruct";
var claimsConfigSchema = object10({
  filterableProperties: optional10(record3(string8(), any())),
  featureFlags: optional10(
    partial(
      object10({
        shouldShowClaimDetails: boolean8(),
        shouldShowDownloadClaims: boolean8(),
        shouldShowFilterChips: boolean8()
      })
    )
  ),
  supportLink: optional10(string8()),
  showTotalCostOverviewAndAdditionalResources: optional10(boolean8()),
  showClaimStatusDefinitions: optional10(boolean8()),
  showClaimsSummaryBackButton: optional10(boolean8()),
  enableDynamicFilters: optional10(boolean8()),
  enableTabFilters: optional10(boolean8()),
  getTotalCostOverviewVisibilityForClaim: optional10(
    func5()
  ),
  slots: optional10(
    object10({
      Banner: optional10(func5()),
      ServiceDetailsBanner: optional10(
        func5()
      )
    })
  ),
  showHealthInsuranceGlossary: optional10(boolean8()),
  healthInsuranceGlossaryLink: optional10(string8())
});

// src/module-config-schemas/care-discovery.ts
import { boolean as boolean9, object as object11, optional as optional11, string as string9, enums as enums3 } from "superstruct";
var careDiscoveryConfigSchema = object11({
  showGetAssistanceNowSubheading: optional11(boolean9()),
  showProviderAvailability: optional11(boolean9()),
  masonryAppId: optional11(
    object11({
      left: string9(),
      right: optional11(string9())
    })
  ),
  masonryAppVersion: optional11(enums3(["1.5", "2.0"]))
});

// src/module-config-schemas/messaging.ts
import { object as object12, array, number as number2, string as string10 } from "superstruct";
var messagingConfigSchema = object12({
  maxFileSize: number2(),
  allowedFileTypes: string10(),
  allowedFileTypesErrorList: string10(),
  allowedImageTypes: array(string10()),
  messageInputCharacterLength: number2()
});

// src/module-config-schemas/wallet.ts
import { object as object13, optional as optional12, boolean as boolean10, string as string11, func as func6 } from "superstruct";
var walletConfigSchema = object13({
  showBenefitDetailFeedbackComponent: optional12(boolean10()),
  showLandingPageHeaderBranding: optional12(boolean10()),
  showBenefitStatusBannerCreated: optional12(boolean10()),
  showBenefitStatusBannerPending: optional12(boolean10()),
  showBenefitStatusBannerExpired: optional12(boolean10()),
  showLandingPageClaimsEntry: optional12(boolean10()),
  enableNoNetworkLiveBalances: optional12(boolean10()),
  typeformIds: optional12(
    object13({
      en: string11(),
      es: string11(),
      fr: string11()
    })
  ),
  slots: optional12(
    object13({
      Banner: optional12(func6())
    })
  )
});

// src/module-config-schemas/live-chat.ts
import { func as func7, object as object14, type as type2, optional as optional13, boolean as boolean11 } from "superstruct";
var liveChatConfigSchema = object14({
  liveChatClient: type2({
    handleUserChatEvents: func7(),
    subscribeToAgentChatEvents: func7()
  }),
  shouldShowNavigateAway: optional13(boolean11())
});

// src/module-config-schemas/health-journey.ts
import { array as array2, string as string12, optional as optional14, object as object15, enums as enums4, boolean as boolean12 } from "superstruct";
var JourneyTabName = Object.freeze({
  Activities: "activities",
  Progress: "progress",
  History: "history",
  Explore: "explore"
});
var JourneyTabs = Object.values(JourneyTabName);
var healthJourneyConfigSchema = object15({
  tabs: optional14(array2(enums4(JourneyTabs))),
  headerConfig: optional14(
    object15({
      backgroundImageSrc: optional14(string12()),
      disableBackgroundImage: optional14(boolean12()),
      enableHeaderSubTitle: optional14(boolean12()),
      alignment: optional14(enums4(["start", "center", "end"]))
    })
  )
});

// src/module-config-schemas/care-documents.ts
import { boolean as boolean13, object as object16, optional as optional15 } from "superstruct";
var careDocumentsConfigSchema = object16({
  shouldUseDocumentsV2: optional15(boolean13())
});

// src/league-config-schema.ts
var knownLeagueConfigSchema = type3({
  core: coreConfigSchema,
  assets: optional16(assetConfigConfigSchema),
  benefits: optional16(benefitsConfigSchema),
  healthJourney: optional16(healthJourneyConfigSchema),
  careCollections: optional16(careCollectionsConfigSchema),
  careDiscovery: optional16(careDiscoveryConfigSchema),
  messaging: optional16(messagingConfigSchema),
  wallet: optional16(walletConfigSchema),
  claims: optional16(claimsConfigSchema),
  liveChat: optional16(liveChatConfigSchema),
  exampleModule: optional16(exampleModuleConfigSchema),
  dashboard: optional16(dashboardConfigSchema),
  careDocuments: optional16(careDocumentsConfigSchema),
  __internal: optional16(object17())
});
var unknownLeagueConfigSchema = record4(string13(), unknown2());
var leagueConfigSchema = intersection2([
  knownLeagueConfigSchema,
  unknownLeagueConfigSchema
]);

// src/assert-config-schema.ts
import { assert, StructError } from "superstruct";
function assertConfigSchema(value, schema) {
  try {
    assert(value, schema);
  } catch (error) {
    if (!(error instanceof StructError)) {
      throw error;
    }
    const messages = [];
    error.failures().forEach((failure) => {
      const pathStr = failure.path.join(".");
      const pathBase = failure.path.slice(0, -1);
      const pathBaseStr = pathBase.join(".");
      const isRoot = !failure.path.length;
      if (failure.value === void 0) {
        if (isRoot) {
          messages.push("No League config object was provided");
        } else if (failure.path.length < 2) {
          messages.push(
            `Property "${failure.key}" is required but missing in the League config object.`
          );
        } else {
          messages.push(
            `Property "${failure.key}" is required but missing on "${pathBaseStr}".`
          );
        }
      } else if (failure.type === "never") {
        messages.push(
          `"${failure.key}" is not a valid property on "${pathBaseStr}".`
        );
      } else if (isRoot) {
        messages.push(
          `Expected a valid League config object, but got "${failure.value}".`
        );
      } else {
        messages.push(
          `The value "${failure.value}" is invalid for property "${pathStr}"`
        );
      }
    });
    throw new LeagueConfigError(
      `Errors found in League configuration!

${messages.map((message) => `- ${message}`).join("\n")}`
    );
  }
}

// src/store.types.ts
var ConfigStoreStatus = /* @__PURE__ */ ((ConfigStoreStatus2) => {
  ConfigStoreStatus2[ConfigStoreStatus2["Empty"] = 0] = "Empty";
  ConfigStoreStatus2[ConfigStoreStatus2["ClientOnly"] = 1] = "ClientOnly";
  ConfigStoreStatus2[ConfigStoreStatus2["Loading"] = 2] = "Loading";
  ConfigStoreStatus2[ConfigStoreStatus2["LoadingWithCache"] = 3] = "LoadingWithCache";
  ConfigStoreStatus2[ConfigStoreStatus2["Success"] = 4] = "Success";
  ConfigStoreStatus2[ConfigStoreStatus2["ErrorWithCache"] = 5] = "ErrorWithCache";
  ConfigStoreStatus2[ConfigStoreStatus2["Error"] = 6] = "Error";
  return ConfigStoreStatus2;
})(ConfigStoreStatus || {});

// src/module-config-schemas/server-driven.ts
import {
  optional as optional17,
  string as string14,
  boolean as boolean14,
  number as number3,
  integer,
  record as record5,
  unknown as unknown3,
  intersection as intersection3,
  type as type4,
  object as object18
} from "superstruct";
var knownServerConfigSchema = type4({
  "config_core_tenant-id": optional17(string14()),
  "config_observability_sentry-dsn": optional17(string14()),
  "config_observability_sentry-max-breadcrumbs": optional17(integer()),
  "config_observability_sentry-errors-sample-rate": optional17(number3()),
  "config_observability_sentry-traces-sample-rate": optional17(number3()),
  "config_observability_custom-view-tracing": optional17(boolean14()),
  config_observability_enabled: optional17(boolean14()),
  "config_analytics_segment-key": optional17(string14()),
  "config_core-web": optional17(
    object18({
      mobileAppUrls: optional17(
        object18({
          appStoreUrl: optional17(string14()),
          playStoreUrl: optional17(string14())
        })
      )
    })
  ),
  // About Me
  "config_about-me_health-report-masonry-id": optional17(string14()),
  "flag_about-me_show-account-banner": optional17(boolean14()),
  "flag_about-me_show-options-menu": optional17(boolean14()),
  "flag_about-me_show-landing-page-back-button": optional17(boolean14()),
  release_about_me_masonry_2_for_metric_details: optional17(boolean14()),
  // Health Profile
  "flag_health-profile_ratings-bar-enabled": optional17(boolean14()),
  "flag_health-profile_care-sdk-enabled": optional17(boolean14()),
  "flag_health-profile_chat-with-professional-enabled": optional17(boolean14()),
  // Journey Configuration
  config_about_me_care_library: optional17(boolean14()),
  flag_journey_enable_masonry_1_5_for_activities: optional17(boolean14()),
  release_journey_experience_masonry_2_for_activities: optional17(boolean14()),
  release_journey_experience_masonry_2_for_campaign_overview: optional17(
    boolean14()
  )
});
var unknownServerConfigSchema = record5(string14(), unknown3());
var serverConfigSchema = intersection3([
  knownServerConfigSchema,
  unknownServerConfigSchema
]);

// src/store.utils.ts
var serverConfigPaths = Object.keys(knownServerConfigSchema.schema);
function getIsServerConfigPath(path) {
  var _a;
  const firstSegment = (_a = path.match(/^([^.]+)\.?/)) == null ? void 0 : _a[1];
  const is = firstSegment && serverConfigPaths.includes(firstSegment);
  return is;
}
var isStoreEmpty = (state) => state.status === 0 /* Empty */;
var isStatusSettled = (status) => status === 4 /* Success */ || status === 5 /* ErrorWithCache */ || status === 3 /* LoadingWithCache */;
var isConfigSettled = (configWithStatus) => isStatusSettled(configWithStatus.status);
var isFetchStatusSettled = (status) => status === 4 /* Success */ || status === 5 /* ErrorWithCache */;
var isServerConfigFetchSettled = (state) => isFetchStatusSettled(state.status);

// src/store.constants.ts
var GETTERS_WITHOUT_CONFIG_ERROR_MESSAGE = "Trying to use config getters, but League config has not been set! Are you sure you called `setConfig` at least once, or that a config object was passed to `<LeagueProvider>` at the top of your app?";
var SET_CONFIG_UPDATER_WITHOUT_CONFIG_ERROR_MESSAGE = "Trying to use `setConfig` with an updater function, but League config has not been initialized! Are you sure you called `setConfig` at least once, or that a config object was passed to `<LeagueProvider>` at the top of your app?";
var FAILED_TO_FETCH_SERVER_CONFIG_ERROR_MESSAGE = "Failed to fetch configuration from the server";

// src/store.ts
var configStore = createStore(() => ({
  config: void 0,
  serverConfig: void 0,
  status: 0 /* Empty */
}));
function setConfig(configOrRecipe) {
  configStore.setState((currentState) => {
    let newConfig;
    const isEmpty = isStoreEmpty(currentState);
    if (typeof configOrRecipe === "function") {
      if (isEmpty) {
        throw new LeagueConfigError(
          SET_CONFIG_UPDATER_WITHOUT_CONFIG_ERROR_MESSAGE
        );
      }
      newConfig = produce(currentState.config, configOrRecipe);
    } else {
      newConfig = configOrRecipe;
    }
    assertConfigSchema(newConfig, leagueConfigSchema);
    if (isEmpty) {
      return {
        config: newConfig,
        status: 1 /* ClientOnly */
      };
    }
    return {
      config: newConfig
    };
  });
}
function getMergedConfigFromState(state) {
  return isStoreEmpty(state) ? void 0 : __spreadValues(__spreadValues({}, state.serverConfig), state.config);
}
function getConfig(assertLeagueConfigSet = true) {
  const state = configStore.getState();
  if (!isStoreEmpty(state)) {
    return {
      status: state.status,
      config: getMergedConfigFromState(state)
    };
  }
  if (assertLeagueConfigSet) {
    throw new LeagueConfigError(GETTERS_WITHOUT_CONFIG_ERROR_MESSAGE);
  }
  return {
    status: state.status,
    config: void 0
  };
}
function subscribeToConfig(selector, listener) {
  const initialState = configStore.getState();
  let firstSelection;
  const firstConfig = getMergedConfigFromState(initialState);
  if (firstConfig) {
    const configWithStatus = {
      status: initialState.status,
      config: firstConfig
    };
    firstSelection = selector(configWithStatus);
  } else {
    firstSelection = void 0;
  }
  listener(firstSelection, void 0);
  let prevSelection = firstSelection;
  const unsubscribe = configStore.subscribe((state) => {
    const config = getMergedConfigFromState(state);
    let selection;
    if (config) {
      const configWithStatus = {
        status: state.status,
        config
      };
      selection = selector(configWithStatus);
    } else {
      selection = void 0;
    }
    if (!shallow(prevSelection, selection)) {
      const originalPrevSelection = prevSelection;
      prevSelection = selection;
      listener(selection, originalPrevSelection);
    }
  });
  return unsubscribe;
}
var getSettledConfig = () => __async(void 0, null, function* () {
  return new Promise((resolve, reject) => {
    const initialState = configStore.getState();
    if (isServerConfigFetchSettled(initialState)) {
      resolve(getMergedConfigFromState(initialState));
    } else if (initialState.status === 6 /* Error */)
      reject(new Error(FAILED_TO_FETCH_SERVER_CONFIG_ERROR_MESSAGE));
    else {
      const unsubscribe = configStore.subscribe((currentState) => {
        if (isServerConfigFetchSettled(currentState)) {
          unsubscribe();
          resolve(getMergedConfigFromState(currentState));
        } else if (currentState.status === 6 /* Error */) {
          unsubscribe();
          reject(new Error(FAILED_TO_FETCH_SERVER_CONFIG_ERROR_MESSAGE));
        }
      });
    }
  });
});
function getServerConfigSelectionStatus(status) {
  return {
    isPresent: isStatusSettled(status),
    isLoading: status === 3 /* LoadingWithCache */ || status === 2 /* Loading */,
    isError: status === 6 /* Error */,
    isFresh: status === 4 /* Success */
  };
}
function useConfigSelection(selector, defaultValue) {
  return useStoreWithEqualityFn(
    configStore,
    (state) => {
      if (state.status === 0 /* Empty */) {
        throw new LeagueConfigError(GETTERS_WITHOUT_CONFIG_ERROR_MESSAGE);
      }
      const config = getMergedConfigFromState(state);
      const configWithStatus = {
        status: state.status,
        config
      };
      const selection = selector(configWithStatus);
      if (typeof defaultValue === "object") {
        return mergeWith(
          {},
          defaultValue,
          selection,
          (_, b) => Array.isArray(b) ? b : void 0
        );
      }
      return selection != null ? selection : defaultValue;
    },
    shallow
  );
}
function useConfigProperty(path, defaultValue) {
  const isServerConfigPath = getIsServerConfigPath(path);
  const serverConfigStatus = useStore(
    configStore,
    ({ status }) => isServerConfigPath ? status : 4 /* Success */
  );
  const configSelection = useConfigSelection(
    ({ config }) => get(config, path),
    defaultValue
  );
  if (!isServerConfigPath)
    return configSelection;
  return __spreadValues({
    data: configSelection
  }, getServerConfigSelectionStatus(serverConfigStatus));
}
function useUnknownConfigProperty(path, defaultValue) {
  const configSelection = useConfigSelection(
    ({ config }) => get(config, path),
    defaultValue
  );
  return configSelection;
}

// src/server-driven-config.ts
import { backOff } from "exponential-backoff";
function getCacheKey({
  clientId,
  environment,
  apiVersion
}) {
  return `server-config-${apiVersion}-${clientId}-${environment}`;
}
function getServerConfigURL({ apiVersion }) {
  var _a;
  const apiUrl = (_a = configStore.getState().config) == null ? void 0 : _a.core.api.url;
  if (!apiUrl)
    throw new Error("No API URL provided in League configuration");
  return `${apiUrl}/${apiVersion}/sdk-configs`;
}
function getSegmentationQueryParams(segmentationParams) {
  const queryKeysToExtract = [
    "sdkVersion",
    "appVersion",
    "browserVersion",
    "browserName"
  ];
  const queryParams = [];
  queryKeysToExtract.forEach((key) => {
    const value = segmentationParams[key];
    if (value) {
      queryParams.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    }
  });
  return queryParams.join("&");
}
function cacheServerConfig(segmentationParameters, config) {
  localStorage.setItem(
    getCacheKey(segmentationParameters),
    JSON.stringify(config)
  );
}
function getCachedServerConfig(segmentationParameters) {
  const serverConfigString = localStorage.getItem(
    getCacheKey(segmentationParameters)
  );
  return serverConfigString ? JSON.parse(serverConfigString) : void 0;
}
function fetchServerConfig(_0, _1) {
  return __async(this, arguments, function* (url, { clientId }) {
    let response;
    try {
      response = yield fetch(url, {
        headers: { "X-League-Client-Id": clientId }
      });
    } catch (fetchError) {
      throw Error(`Fetch server driven config failure: ${fetchError}`);
    }
    if (!response.ok) {
      console.error(
        `Fetch server driven config error response`,
        response.status,
        response.statusText
      );
      throw Error(
        `Fetch server driven config error response: ${response.status} ${response.statusText}`
      );
    }
    let serverConfigResponse;
    try {
      const responseJson = yield response.json();
      serverConfigResponse = responseJson.data.attributes;
    } catch (parseError) {
      throw Error(`Error parsing server response as JSON: ${parseError}`);
    }
    assertConfigSchema(serverConfigResponse, serverConfigSchema);
    return serverConfigResponse;
  });
}
function updateServerConfig(segmentationParameters) {
  return __async(this, null, function* () {
    const serverConfigUrl = getServerConfigURL(segmentationParameters);
    const queryParams = getSegmentationQueryParams(segmentationParameters);
    const serverConfigUrlWithQueryParams = queryParams ? `${serverConfigUrl}?${queryParams}` : serverConfigUrl;
    const cachedServerConfig = getCachedServerConfig(segmentationParameters);
    if (cachedServerConfig) {
      configStore.setState({
        serverConfig: cachedServerConfig,
        status: 3 /* LoadingWithCache */
      });
    } else {
      configStore.setState({
        serverConfig: void 0,
        status: 2 /* Loading */
      });
    }
    try {
      const freshServerConfig = yield backOff(
        () => fetchServerConfig(
          serverConfigUrlWithQueryParams,
          segmentationParameters
        ),
        {
          startingDelay: 200,
          numOfAttempts: 6
        }
      );
      configStore.setState({
        serverConfig: freshServerConfig,
        status: 4 /* Success */
      });
      cacheServerConfig(segmentationParameters, freshServerConfig);
    } catch (e) {
      console.error(e);
      if (cachedServerConfig) {
        configStore.setState({ status: 5 /* ErrorWithCache */ });
      } else {
        configStore.setState({ status: 6 /* Error */ });
      }
      throw e;
    }
  });
}

// src/index.ts
var MODULE_VERSION = "2.28.1";
export {
  ConfigStoreStatus,
  LeagueConfigError,
  MODULE_VERSION,
  getConfig,
  getSettledConfig,
  isConfigSettled,
  setConfig,
  subscribeToConfig,
  updateServerConfig,
  useConfigProperty,
  useConfigSelection,
  useUnknownConfigProperty
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/config'] = window.leagueSDKsPresent['@leagueplatform/config'] || [];
    window.leagueSDKsPresent['@leagueplatform/config'].push('2.28.1');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/config'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/config module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map